import { useEffect, useMemo, useState } from "react";

import { ArrowSquareOut } from "@phosphor-icons/react";
import {
  DataRow,
  DataTable,
  LoadingOverlay,
  PaginationFooter,
  ProfileTabs,
  buildPageList,
} from "@breakingthelines/design-system";

import { handleTabArrowKeyDown } from "@/components/common/tab-keyboard-nav";
import { listJudgeLog, type JudgeLogEntry, type PageInfo } from "@/lib/admin-api";

import styles from "./judge-log.module.css";

const VERDICT_TABS = ["All", "Spam", "Clean"] as const;
type VerdictTab = (typeof VERDICT_TABS)[number];

const VERDICT_PARAM: Record<VerdictTab, string | undefined> = {
  All: undefined,
  Spam: "spam",
  Clean: "clean",
};

const PER_PAGE_OPTIONS = [10, 20, 50];

// Reasoning excerpts run long ("llm judge: undisclosed sponsored links; thin
// body; ..."). Collapsed to one line by default; a toggle reveals the rest
// rather than a table cell silently growing to whatever the judge wrote.
const REASONING_EXCERPT_LENGTH = 140;

function tierClass(tier?: string): string {
  switch ((tier ?? "").toUpperCase()) {
    case "HIGH":
      return styles.tierHigh;
    case "MEDIUM":
      return styles.tierMedium;
    case "LOW":
      return styles.tierLow;
    default:
      return styles.tierNone;
  }
}

function verdictClass(verdict?: string): string {
  return verdict === "spam" ? styles.verdictSpam : styles.verdictClean;
}

function formatContentType(type?: string): string {
  if (!type) return "—";
  return type
    .replace("CONTENT_TYPE_", "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

function formatDate(value?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function ReasoningCell({ entry }: { entry: JudgeLogEntry }) {
  const [expanded, setExpanded] = useState(false);

  if (!entry.flagReasoning) {
    // A clean verdict writes no flag, so there is no reasoning text to show:
    // this is the true state, not a load failure.
    return <span className={styles.reasoningEmpty}>None. The verdict was clean.</span>;
  }

  const isLong = entry.flagReasoning.length > REASONING_EXCERPT_LENGTH;
  const text =
    expanded || !isLong
      ? entry.flagReasoning
      : `${entry.flagReasoning.slice(0, REASONING_EXCERPT_LENGTH)}…`;

  return (
    <div className={styles.reasoning}>
      <span>{text}</span>
      {isLong ? (
        <button
          type="button"
          className={styles.reasoningToggle}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      ) : null}
    </div>
  );
}

export function JudgeLogPage() {
  const [verdictTab, setVerdictTab] = useState<VerdictTab>("All");
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(PER_PAGE_OPTIONS[0]);
  const [items, setItems] = useState<JudgeLogEntry[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setPageIndex(1);
  }, [verdictTab]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);

    listJudgeLog({ verdict: VERDICT_PARAM[verdictTab], pageSize, page: pageIndex })
      .then((response) => {
        if (cancelled) return;
        setItems(response.items ?? []);
        setPageInfo(response.pageInfo ?? null);
      })
      .catch(() => {
        // Degrade to the same honest-empty state a genuinely empty page shows —
        // never a raw error string. See moderation-probe/queue's own fetch
        // failure handling for the same rule.
        if (cancelled) return;
        setItems([]);
        setPageInfo(null);
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [verdictTab, pageSize, pageIndex]);

  const totalCount = pageInfo?.totalCount ?? items.length;
  const totalLabel = `Decisions: ${totalCount}`;

  const totalPages = useMemo(() => {
    if (totalCount > 0) return Math.max(1, Math.ceil(totalCount / pageSize));
    return pageIndex;
  }, [totalCount, pageIndex, pageSize]);

  const pages = useMemo(() => buildPageList(totalPages, pageIndex), [totalPages, pageIndex]);
  const activePage = Math.min(pageIndex, totalPages);
  const hasPrevious = pageIndex > 1;
  const hasNext = pageInfo?.hasNextPage ?? (items.length === pageSize && items.length > 0);

  return (
    <div className={styles.page}>
      <div>
        <h1 className={styles.title}>Judge</h1>
        <p className={styles.subtitle}>
          Every decision the moderation judge has recorded, newest first. Read only. Flags are
          actioned from the Queue tab.
        </p>
      </div>

      <div className={styles.toolbar}>
        <ProfileTabs
          role="tablist"
          aria-label="Judge verdict"
          tabs={VERDICT_TABS.map((tab) => ({ id: tab, label: tab }))}
          activeTab={verdictTab}
          onKeyDown={handleTabArrowKeyDown}
          onTabChange={(tab) => setVerdictTab(tab as VerdictTab)}
        />
      </div>

      <section className={styles.listSection}>
        {loading ? <LoadingOverlay label="Loading decisions" /> : null}

        <DataTable
          columns="minmax(200px, 1.2fr) minmax(100px, 0.6fr) minmax(260px, 2fr) minmax(140px, 0.9fr) minmax(120px, 0.7fr)"
          header={["Content", "Verdict", "Reasoning", "Evaluated", "Links"]}
          headerClassName={styles.tableHeader}
        >
          {items.map((entry) => (
            <DataRow key={entry.contentId} className={styles.tableRow}>
              <div className={styles.contentCell} data-label="Content">
                <span className={styles.contentTitle} title={entry.contentTitle || undefined}>
                  {entry.contentTitle || entry.contentId}
                </span>
                <span className={styles.contentType} title={entry.contentId}>
                  {formatContentType(entry.contentType)}
                  {entry.contentTitle ? ` · ${entry.contentId}` : ""}
                </span>
              </div>

              <div className={styles.verdictCell} data-label="Verdict">
                <span className={`${styles.verdictChip} ${verdictClass(entry.verdict)}`}>
                  {entry.verdict || "—"}
                </span>
                <span className={`${styles.tierChip} ${tierClass(entry.tier)}`}>
                  {(entry.tier ?? "—").toUpperCase()}
                </span>
              </div>

              <div data-label="Reasoning">
                <ReasoningCell entry={entry} />
              </div>

              <span className={styles.date} data-label="Evaluated">
                {formatDate(entry.evaluatedAt)}
              </span>

              <div className={styles.links}>
                <a href={`/content/${entry.contentId}`} className={styles.link}>
                  Content <ArrowSquareOut size={12} />
                </a>
                {entry.flagId ? (
                  <a href={`/content/${entry.contentId}`} className={styles.link}>
                    Queue entry <ArrowSquareOut size={12} />
                  </a>
                ) : null}
              </div>
            </DataRow>
          ))}
        </DataTable>

        {!loading && items.length === 0 ? (
          <div className={styles.emptyState}>
            {failed ? "No decisions to show right now." : "No decisions recorded yet."}
          </div>
        ) : null}
      </section>

      <PaginationFooter
        totalLabel={totalLabel}
        pages={pages}
        activePage={activePage}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        onPrevious={() => setPageIndex((p) => Math.max(1, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        perPage={pageSize}
        perPageOptions={PER_PAGE_OPTIONS}
        onPerPageChange={(option) => {
          setPageSize(option);
          setPageIndex(1);
        }}
        onPageChange={(page) => {
          if (page === activePage) return;
          setPageIndex(page);
        }}
        className={styles.pagination}
      />
    </div>
  );
}
